/**
 * FilePreview Component - Full file preview for VoxTerm Commander
 *
 * Adapted from NGUI FilePreview for VoxTerm dark theme.
 * Supports multiple file types:
 * - Code files (syntax highlighting)
 * - Markdown (rendered with navigation sidebar)
 * - Images (zoom controls)
 * - PDF (page navigation)
 * - DOCX (rendered to HTML)
 * - XLSX (rendered as table)
 * - HTML (sandboxed iframe)
 * - Text (monospace pre-formatted)
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vs2015 } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Document, Page, pdfjs } from 'react-pdf';
import * as XLSX from 'xlsx';

// Import markdown navigation utilities
import { parseMarkdownHeaders, buildNavigationTree } from '../../utils/markdownNav';
import type { NavigationNode } from '../../utils/markdownNav';
import { useScrollSpy } from '../../hooks/useScrollSpy';
import NavigationTree from './NavigationTree';

// Import commander API
import * as commanderApi from '../../services/commanderApi';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Import syntax highlighting languages
import javascript from 'react-syntax-highlighter/dist/esm/languages/hljs/javascript';
import typescript from 'react-syntax-highlighter/dist/esm/languages/hljs/typescript';
import python from 'react-syntax-highlighter/dist/esm/languages/hljs/python';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import sql from 'react-syntax-highlighter/dist/esm/languages/hljs/sql';
import css from 'react-syntax-highlighter/dist/esm/languages/hljs/css';
import xml from 'react-syntax-highlighter/dist/esm/languages/hljs/xml';
import bash from 'react-syntax-highlighter/dist/esm/languages/hljs/bash';
import markdown from 'react-syntax-highlighter/dist/esm/languages/hljs/markdown';
import yaml from 'react-syntax-highlighter/dist/esm/languages/hljs/yaml';

// Register languages
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('xml', xml);
SyntaxHighlighter.registerLanguage('html', xml);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('markdown', markdown);
SyntaxHighlighter.registerLanguage('yaml', yaml);

// Types
type FileType = 'code' | 'markdown' | 'image' | 'pdf' | 'docx' | 'xlsx' | 'html' | 'text' | 'binary';

interface FilePreviewProps {
  disk: string;
  path: string;
  fileName: string;
  onClose: () => void;
}

// File type detection
const getFileType = (fileName: string): FileType => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';

  // Code files
  const codeExtensions = [
    'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'h', 'cs',
    'php', 'rb', 'go', 'rs', 'swift', 'kt', 'sh', 'bash', 'sql',
    'css', 'scss', 'less', 'yaml', 'yml', 'toml', 'ini', 'conf'
  ];
  if (codeExtensions.includes(ext)) return 'code';

  // Markdown
  if (ext === 'md' || ext === 'markdown') return 'markdown';

  // Images
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico'];
  if (imageExtensions.includes(ext)) return 'image';

  // PDF
  if (ext === 'pdf') return 'pdf';

  // DOCX
  if (ext === 'docx' || ext === 'doc') return 'docx';

  // XLSX
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') return 'xlsx';

  // HTML
  if (ext === 'html' || ext === 'htm') return 'html';

  // JSON (special case - render as code)
  if (ext === 'json') return 'code';

  // Text files
  const textExtensions = ['txt', 'log', 'env', 'gitignore', 'dockerignore'];
  if (textExtensions.includes(ext)) return 'text';

  return 'binary';
};

// Get syntax highlighter language
const getLanguage = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const languageMap: Record<string, string> = {
    'js': 'javascript',
    'jsx': 'javascript',
    'ts': 'typescript',
    'tsx': 'typescript',
    'py': 'python',
    'json': 'json',
    'sql': 'sql',
    'css': 'css',
    'scss': 'css',
    'less': 'css',
    'html': 'html',
    'htm': 'html',
    'xml': 'xml',
    'sh': 'bash',
    'bash': 'bash',
    'md': 'markdown',
    'yaml': 'yaml',
    'yml': 'yaml',
  };
  return languageMap[ext] || 'plaintext';
};

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export default function FilePreview({ disk, path, fileName, onClose }: FilePreviewProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | ArrayBuffer | null>(null);
  const [fileBlob, setFileBlob] = useState<Blob | null>(null);
  const [fileSize, setFileSize] = useState<number>(0);
  const [fileType, setFileType] = useState<FileType>('binary');

  // Image zoom state
  const [imageZoom, setImageZoom] = useState(100);

  // PDF state
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);

  // XLSX state
  const [xlsxHtml, setXlsxHtml] = useState<string>('');
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [currentSheet, setCurrentSheet] = useState(0);
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);

  // DOCX container ref
  const docxContainerRef = useRef<HTMLDivElement>(null);

  // Markdown navigation state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Build navigation structure from markdown content
  const { tree, elementIds } = useMemo(() => {
    if (fileType !== 'markdown' || !fileContent || typeof fileContent !== 'string') {
      return { tree: [] as NavigationNode[], elementIds: [] as string[] };
    }
    const parsed = parseMarkdownHeaders(fileContent);
    const builtTree = buildNavigationTree(parsed);
    const ids = parsed.map((h) => h.id);
    return { tree: builtTree, elementIds: ids };
  }, [fileType, fileContent]);

  // Scroll spy for tracking active section
  const activeElementId = useScrollSpy(elementIds, { containerRef: contentRef });

  // Scroll to element in markdown content
  const scrollToElement = useCallback((elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Auto-scroll sidebar to show active element
  useEffect(() => {
    if (activeElementId && sidebarRef.current) {
      const activeNavItem = sidebarRef.current.querySelector(`[data-nav-id="${activeElementId}"]`);
      if (activeNavItem) {
        const sidebar = sidebarRef.current;
        const itemRect = activeNavItem.getBoundingClientRect();
        const sidebarRect = sidebar.getBoundingClientRect();

        if (itemRect.top < sidebarRect.top || itemRect.bottom > sidebarRect.bottom) {
          activeNavItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  }, [activeElementId]);

  // Fetch file content from Brain
  const fetchFile = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const detectedType = getFileType(fileName);
      setFileType(detectedType);

      if (detectedType === 'image') {
        // For images, get preview URL
        const previewUrl = await commanderApi.getPreview(disk, path, 1024);
        setFileContent(previewUrl);
        setFileSize(0);
      } else if (detectedType === 'pdf' || detectedType === 'docx' || detectedType === 'xlsx') {
        // For binary documents, download as blob
        const response = await fetch(`/api/files/download?disk=${encodeURIComponent(disk)}&path=${encodeURIComponent(path)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('ngui_auth_token')}`
          }
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.status}`);
        }
        const blob = await response.blob();
        setFileBlob(blob);
        setFileSize(blob.size);

        if (detectedType === 'pdf') {
          setFileContent(URL.createObjectURL(blob));
        } else if (detectedType === 'docx') {
          const arrayBuffer = await blob.arrayBuffer();
          const { renderAsync } = await import('docx-preview');
          if (docxContainerRef.current) {
            await renderAsync(arrayBuffer, docxContainerRef.current, undefined, {
              className: 'docx-preview-container',
              inWrapper: true,
              ignoreWidth: false,
              ignoreHeight: false,
              ignoreFonts: false,
              breakPages: true,
              ignoreLastRenderedPageBreak: true,
              experimental: false,
              trimXmlDeclaration: true,
              useBase64URL: true,
              renderHeaders: true,
              renderFooters: true,
              renderFootnotes: true,
              renderEndnotes: true,
            });
          }
          setFileContent('docx-rendered');
        } else if (detectedType === 'xlsx') {
          const arrayBuffer = await blob.arrayBuffer();
          const wb = XLSX.read(arrayBuffer, { type: 'array' });
          setWorkbook(wb);
          setSheetNames(wb.SheetNames);
          setCurrentSheet(0);
          const html = XLSX.utils.sheet_to_html(wb.Sheets[wb.SheetNames[0]], {
            editable: false,
          });
          setXlsxHtml(html);
          setFileContent('xlsx-rendered');
        }
      } else if (detectedType === 'binary') {
        setFileContent(null);
      } else {
        // For text-based files, use Brain's file.read command
        const content = await commanderApi.getFileContent(disk, path);
        setFileContent(content);
        setFileSize(new Blob([content]).size);
      }
    } catch (err) {
      console.error('Failed to load file:', err);
      setError(err instanceof Error ? err.message : 'Failed to load file');
    } finally {
      setLoading(false);
    }
  }, [disk, path, fileName]);

  useEffect(() => {
    fetchFile();

    return () => {
      if (fileContent && typeof fileContent === 'string' && fileContent.startsWith('blob:')) {
        URL.revokeObjectURL(fileContent);
      }
    };
  }, [fetchFile]);

  // Handle sheet change for XLSX
  const handleSheetChange = (index: number) => {
    if (workbook && workbook.SheetNames[index]) {
      setCurrentSheet(index);
      const html = XLSX.utils.sheet_to_html(workbook.Sheets[workbook.SheetNames[index]], {
        editable: false,
      });
      setXlsxHtml(html);
    }
  };

  // Handle download
  const handleDownload = async () => {
    await commanderApi.downloadFile(disk, path, fileName);
  };

  // Render content based on file type
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-blue-400 border-t-blue-600 rounded-full animate-spin" />
            <p className="text-gray-400">Loading file...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="text-6xl mb-4">:(</div>
            <h2 className="text-xl font-semibold mb-2 text-gray-200">Failed to load file</h2>
            <p className="text-gray-400 mb-6">{error}</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      );
    }

    switch (fileType) {
      case 'code':
        return (
          <div className="h-full overflow-auto bg-[#1e1e1e]">
            <SyntaxHighlighter
              language={getLanguage(fileName)}
              style={vs2015}
              showLineNumbers
              wrapLines
              customStyle={{
                margin: 0,
                padding: '1rem',
                fontSize: '14px',
                lineHeight: '1.5',
                minHeight: '100%',
              }}
            >
              {String(fileContent)}
            </SyntaxHighlighter>
          </div>
        );

      case 'markdown': {
        const idCounts: Record<string, number> = {};

        const getHeaderId = (text: string): string => {
          const baseId = String(text)
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');

          let id = baseId;
          if (idCounts[baseId] !== undefined) {
            idCounts[baseId]++;
            id = `${baseId}-${idCounts[baseId]}`;
          } else {
            idCounts[baseId] = 0;
          }
          return id;
        };

        const markdownComponents: Partial<Components> = {
          h1: ({ children, ...props }) => (
            <h1 id={getHeaderId(String(children))} className="scroll-mt-20 text-gray-100" {...props}>{children}</h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 id={getHeaderId(String(children))} className="scroll-mt-20 text-gray-100" {...props}>{children}</h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 id={getHeaderId(String(children))} className="scroll-mt-20 text-gray-100" {...props}>{children}</h3>
          ),
          h4: ({ children, ...props }) => (
            <h4 id={getHeaderId(String(children))} className="scroll-mt-20 text-gray-100" {...props}>{children}</h4>
          ),
          h5: ({ children, ...props }) => (
            <h5 id={getHeaderId(String(children))} className="scroll-mt-20 text-gray-100" {...props}>{children}</h5>
          ),
          h6: ({ children, ...props }) => (
            <h6 id={getHeaderId(String(children))} className="scroll-mt-20 text-gray-100" {...props}>{children}</h6>
          ),
          p: ({ children, ...props }) => (
            <p className="text-gray-300" {...props}>{children}</p>
          ),
          a: ({ children, ...props }) => (
            <a className="text-blue-400 hover:underline" {...props}>{children}</a>
          ),
          code: ({ children, className, ...props }) => {
            const isInline = !className;
            return isInline ? (
              <code className="bg-gray-700 px-1 rounded text-sm text-gray-200" {...props}>{children}</code>
            ) : (
              <code className={className} {...props}>{children}</code>
            );
          },
          pre: ({ children, ...props }) => (
            <pre className="bg-gray-800 p-4 rounded-lg overflow-x-auto" {...props}>{children}</pre>
          ),
          ul: ({ children, ...props }) => (
            <ul className="text-gray-300 list-disc pl-6" {...props}>{children}</ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="text-gray-300 list-decimal pl-6" {...props}>{children}</ol>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote className="border-l-4 border-gray-600 pl-4 italic text-gray-400" {...props}>{children}</blockquote>
          ),
        };

        return (
          <div className="h-full flex">
            {/* Sidebar Navigation */}
            {tree.length > 0 && (
              <div
                className={`flex-shrink-0 bg-gray-800 border-r border-gray-700 transition-all duration-300 ${
                  sidebarOpen ? 'w-72' : 'w-0'
                }`}
              >
                {sidebarOpen && (
                  <div ref={sidebarRef} className="h-full overflow-y-auto p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-sm text-gray-300">Navigation</h3>
                      <button
                        onClick={() => setSidebarOpen(false)}
                        className="p-1 text-gray-500 hover:text-gray-300 rounded"
                        title="Close sidebar"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <NavigationTree
                      nodes={tree}
                      activeId={activeElementId}
                      onNavigate={scrollToElement}
                      title="Contents"
                      showExpandAll={tree.length > 3}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Toggle sidebar button (when closed) */}
            {!sidebarOpen && tree.length > 0 && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="absolute left-4 top-20 z-10 p-2 bg-gray-800 border border-gray-700 rounded-lg shadow-md hover:bg-gray-700"
                title="Open navigation"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}

            {/* Main content */}
            <div ref={contentRef} className="flex-1 overflow-auto bg-gray-900 p-8">
              <div className="max-w-4xl mx-auto bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
                  <h2 className="text-white font-semibold text-lg">{fileName}</h2>
                </div>
                <div className="markdown-content p-8 prose prose-invert max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                  >
                    {String(fileContent)}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'image':
        return (
          <div className="h-full overflow-auto flex items-center justify-center bg-gray-900 p-4">
            <img
              src={String(fileContent)}
              alt={fileName}
              style={{ transform: `scale(${imageZoom / 100})` }}
              className="max-w-full max-h-full object-contain transition-transform"
            />
          </div>
        );

      case 'pdf':
        return (
          <div className="h-full overflow-auto flex flex-col items-center bg-gray-900 p-4">
            <Document
              file={String(fileContent)}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={
                <div className="flex items-center gap-2 text-gray-400">
                  <div className="w-6 h-6 border-2 border-blue-400 border-t-blue-600 rounded-full animate-spin" />
                  <span>Loading PDF...</span>
                </div>
              }
            >
              <Page
                pageNumber={currentPage}
                renderTextLayer
                renderAnnotationLayer
                className="shadow-lg"
              />
            </Document>
          </div>
        );

      case 'docx':
        return (
          <div className="h-full overflow-auto bg-gray-900 p-4">
            <div
              ref={docxContainerRef}
              className="max-w-4xl mx-auto bg-white shadow-lg"
              style={{ minHeight: '100%' }}
            />
          </div>
        );

      case 'xlsx':
        return (
          <div className="h-full overflow-auto bg-gray-800">
            <div
              className="xlsx-preview p-4"
              dangerouslySetInnerHTML={{ __html: xlsxHtml }}
            />
          </div>
        );

      case 'html':
        return (
          <div className="h-full bg-white">
            <iframe
              srcDoc={String(fileContent)}
              title={fileName}
              className="w-full h-full border-0"
              sandbox="allow-same-origin"
            />
          </div>
        );

      case 'text':
        return (
          <div className="h-full overflow-auto bg-gray-900 p-4">
            <pre className="font-mono text-sm text-gray-300 whitespace-pre-wrap break-words">
              {String(fileContent)}
            </pre>
          </div>
        );

      case 'binary':
      default:
        return (
          <div className="flex items-center justify-center h-full bg-gray-900">
            <div className="text-center">
              <div className="text-6xl mb-4">📄</div>
              <h2 className="text-xl font-semibold mb-2 text-gray-200">Preview not available</h2>
              <p className="text-gray-400 mb-6">
                This file type cannot be previewed in the browser.
              </p>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Download File
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 h-14 bg-gray-800 border-b border-gray-700 text-white flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            title="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="flex items-center gap-3">
            <span className="font-medium truncate max-w-md">{fileName}</span>
            {fileSize > 0 && (
              <span className="text-sm text-gray-400">{formatFileSize(fileSize)}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Markdown sidebar toggle */}
          {fileType === 'markdown' && tree.length > 0 && !loading && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`p-1.5 rounded transition-colors mr-2 ${
                sidebarOpen ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
              }`}
              title={sidebarOpen ? 'Hide navigation' : 'Show navigation'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" />
              </svg>
            </button>
          )}

          {/* Image zoom controls */}
          {fileType === 'image' && !loading && (
            <div className="flex items-center gap-2 mr-4">
              <button
                onClick={() => setImageZoom(Math.max(25, imageZoom - 25))}
                disabled={imageZoom <= 25}
                className="p-1.5 hover:bg-gray-700 rounded disabled:opacity-50"
                title="Zoom out"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <span className="text-sm w-12 text-center">{imageZoom}%</span>
              <button
                onClick={() => setImageZoom(Math.min(200, imageZoom + 25))}
                disabled={imageZoom >= 200}
                className="p-1.5 hover:bg-gray-700 rounded disabled:opacity-50"
                title="Zoom in"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              <button
                onClick={() => setImageZoom(100)}
                className="px-2 py-1 text-xs hover:bg-gray-700 rounded"
                title="Reset zoom"
              >
                Reset
              </button>
            </div>
          )}

          {/* PDF page controls */}
          {fileType === 'pdf' && numPages > 0 && (
            <div className="flex items-center gap-2 mr-4">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className="p-1.5 hover:bg-gray-700 rounded disabled:opacity-50"
                title="Previous page"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm">
                {currentPage} / {numPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))}
                disabled={currentPage >= numPages}
                className="p-1.5 hover:bg-gray-700 rounded disabled:opacity-50"
                title="Next page"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {/* XLSX sheet tabs */}
          {fileType === 'xlsx' && sheetNames.length > 1 && (
            <div className="flex items-center gap-1 mr-4">
              {sheetNames.map((name, index) => (
                <button
                  key={name}
                  onClick={() => handleSheetChange(index)}
                  className={`px-3 py-1 text-sm rounded transition-colors ${
                    currentSheet === index
                      ? 'bg-blue-600 text-white'
                      : 'hover:bg-gray-700 text-gray-300'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          )}

          {/* Download button */}
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span className="text-sm">Download</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {renderContent()}
      </div>

      {/* XLSX Styles */}
      <style>{`
        .xlsx-preview table {
          border-collapse: collapse;
          width: 100%;
          font-size: 14px;
        }
        .xlsx-preview th,
        .xlsx-preview td {
          border: 1px solid #4b5563;
          padding: 8px 12px;
          text-align: left;
          color: #e5e7eb;
        }
        .xlsx-preview th {
          background-color: #374151;
          font-weight: 600;
        }
        .xlsx-preview tr:hover {
          background-color: #374151;
        }
        .docx-preview-container {
          padding: 20px;
        }
        .prose-invert {
          color: #e5e7eb;
        }
        .prose-invert h1, .prose-invert h2, .prose-invert h3,
        .prose-invert h4, .prose-invert h5, .prose-invert h6 {
          color: #f3f4f6;
        }
        .prose-invert a {
          color: #60a5fa;
        }
        .prose-invert code {
          background-color: #374151;
          color: #e5e7eb;
        }
        .prose-invert pre {
          background-color: #1f2937;
        }
        .prose-invert blockquote {
          border-left-color: #4b5563;
          color: #9ca3af;
        }
        .markdown-content h1 { font-size: 2rem; font-weight: bold; margin: 1.5rem 0 1rem; }
        .markdown-content h2 { font-size: 1.5rem; font-weight: bold; margin: 1.25rem 0 0.75rem; }
        .markdown-content h3 { font-size: 1.25rem; font-weight: bold; margin: 1rem 0 0.5rem; }
        .markdown-content p { margin: 0.75rem 0; }
        .markdown-content ul, .markdown-content ol { margin: 0.75rem 0; }
        .markdown-content li { margin: 0.25rem 0; }
        .markdown-content pre { margin: 1rem 0; }
        .markdown-content blockquote { margin: 1rem 0; }
        .markdown-content table { border-collapse: collapse; margin: 1rem 0; }
        .markdown-content th, .markdown-content td { border: 1px solid #4b5563; padding: 0.5rem 1rem; }
        .markdown-content th { background-color: #374151; }
      `}</style>
    </div>
  );
}
